import React from 'react';
import { Flex, Text, Tooltip } from '@chakra-ui/react';
import { Badge } from '@morressier/alembic';
import { SubmissionAuthorContribution } from '@publishing-engine/domain/models';

export type ContributionsBadgeProps = {
  contributions?: SubmissionAuthorContribution[];
  /** Warn-colours the empty state when the contributions field is mandatory. */
  isMandatory?: boolean;
};

const formatContribution = ({
  contribution,
  degree
}: SubmissionAuthorContribution) =>
  degree ? `${contribution} (${degree})` : contribution;

/**
 * Renders an author's contributions as a badge group. Shows the first
 * contribution with a "+N more" badge that reveals the rest on hover.
 */
export const ContributionsBadge: React.FC<ContributionsBadgeProps> = ({
  contributions,
  isMandatory
}) => {
  if (!contributions?.length) {
    return (
      <Text
        textStyle="textContext"
        color={isMandatory ? 'actionSecondaryCautionContent' : 'GrayText'}
        data-cy="no-contribution-data"
      >
        No data available
      </Text>
    );
  }

  // Sort a copy: callers pass arrays straight out of form state.
  const sortedContributions = [...contributions].sort((a, b) =>
    a.contribution.localeCompare(b.contribution)
  );

  return (
    <Flex gap={2} wrap="wrap">
      <Badge
        size="context"
        variant="neutral"
        wordBreak="break-word"
        whiteSpace="normal"
        maxWidth="full"
        data-cy="contribution-badge"
      >
        {sortedContributions[0]?.contribution}
      </Badge>

      {sortedContributions.length > 1 && (
        <Tooltip
          hasArrow
          shouldWrapChildren
          placement="top"
          borderRadius={4}
          data-cy="contributions-tooltip"
          label={sortedContributions
            .slice(1)
            .map(formatContribution)
            .join(', ')}
        >
          <Badge
            size="context"
            variant="neutral"
            data-cy="more-contributions-badge"
          >
            +{sortedContributions.length - 1} more
          </Badge>
        </Tooltip>
      )}
    </Flex>
  );
};
